import {
  ArrowLeftRight,
  ClipboardCheck,
  Download,
  Pencil,
  Sheet,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import type { OperationType, Permission } from '@lightbm/shared';

/** The everyday operations ("What do you want to do?" and the New operation menu). */
export interface OperationShortcut {
  key: 'receive' | 'issue' | 'move' | 'count' | 'correct' | 'opening';
  icon: LucideIcon;
  /** Icon tile colours, from the home dashboard design. */
  tile: string;
  anyOf: Permission[];
  /** The operation it starts, or the page of a transfer or a count. */
  type?: OperationType;
  path?: string;
  /** Only in the New operation menu (not a daily tile). */
  menuOnly?: boolean;
}

export const OPERATIONS: OperationShortcut[] = [
  {
    key: 'receive',
    icon: Download,
    tile: 'bg-primary-soft text-primary',
    anyOf: ['stock:create'],
    type: 'stockIn',
  },
  {
    key: 'issue',
    icon: Upload,
    tile: 'bg-warning-soft text-warning',
    anyOf: ['stock:create'],
    type: 'stockOut',
  },
  {
    key: 'move',
    icon: ArrowLeftRight,
    tile: 'bg-info-soft text-info',
    anyOf: ['transfers:create'],
    path: '/transfers/new',
  },
  {
    key: 'count',
    icon: ClipboardCheck,
    tile: 'bg-reversed-soft text-reversed',
    anyOf: ['counts:create'],
    path: '/counts?start=1',
  },
  {
    key: 'correct',
    icon: Pencil,
    tile: 'bg-neutral-soft text-ink-2',
    anyOf: ['stock:create'],
    type: 'adjustment',
  },
  {
    key: 'opening',
    icon: Sheet,
    tile: 'bg-info-soft text-info',
    anyOf: ['stock:create'],
    type: 'openingStock',
    menuOnly: true,
  },
];

/** Where a shortcut leads. */
export const shortcutPath = (shortcut: OperationShortcut) =>
  shortcut.type ? newOperationPath(shortcut.type) : (shortcut.path ?? '/');

/** Page of a new operation of this type. */
export const newOperationPath = (type: OperationType, query: Record<string, string> = {}) => {
  const search = new URLSearchParams(query).toString();
  return `/operations/new/${type}${search ? `?${search}` : ''}`;
};
